'use client';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { Tooltip } from 'tdesign-react';
import { useLocale } from 'use-intl';
import {
  fetchConferenceChatAuthInfo,
  getConferenceChatAuthApiBySite,
  isConferenceChatGuestSelf,
  openConferenceChatEntry,
} from '../../../navbar/conferenceChat';
import type { ConferenceChatAuthInfo } from '../../../navbar/conferenceChat';
import Image from '../../../image';
import SectionTitle from './SectionTitle';
import SectionYearSelector from './SectionYearSelector';
import styles from './AgendaSection.module.css';
import { fetchConferenceAgendaDetail } from '../api';
import { formatConferenceAgendaClockRange } from '../agenda.helpers';
import type {
  ConferenceAgendaDetailResp,
  ConferenceAgendaForumItem,
  ConferenceAgendaGuestItem,
  ConferenceAgendaItem,
  ConferenceAgendaSessionItem,
  ConferenceSite,
} from '../types';

interface AgendaSectionProps {
  data?: ConferenceAgendaDetailResp;
  title?: string;
  site?: ConferenceSite;
  years?: number[];
}

const getLocaleText = (isZh: boolean, zh?: string, en?: string) =>
  (isZh ? zh : en) || '';

const getForumList = (data?: ConferenceAgendaDetailResp) =>
  data?.ForumList ||
  data?.forumList ||
  data?.Forums ||
  data?.forums ||
  data?.list ||
  [];

const getSessionList = (forum?: ConferenceAgendaForumItem) =>
  forum?.SessionList ||
  forum?.sessionList ||
  forum?.Sessions ||
  forum?.sessions ||
  [];

const getAgendaList = (session?: ConferenceAgendaSessionItem) =>
  session?.AgendaList ||
  session?.agendaList ||
  session?.Agendas ||
  session?.agendas ||
  session?.Items ||
  session?.items ||
  [];

const getGuestList = (agenda?: ConferenceAgendaItem) =>
  // 后端大会议程接口返回 GuestList，保留 Guests/guests 仅兼容历史占位数据。
  agenda?.GuestList ||
  agenda?.guestList ||
  agenda?.Guests ||
  agenda?.guests ||
  [];

const getForumID = (forum?: ConferenceAgendaForumItem, index = 0) =>
  forum?.ForumID || forum?.forumId || `forum-${index}`;

const getSessionID = (session?: ConferenceAgendaSessionItem, index = 0) =>
  session?.SessionID || session?.sessionId || `session-${index}`;

const getAgendaID = (agenda?: ConferenceAgendaItem, index = 0) =>
  agenda?.AgendaID ||
  agenda?.AgendaItemID ||
  agenda?.agendaId ||
  agenda?.agendaItemId ||
  `agenda-${index}`;

const getGuestName = (guest: ConferenceAgendaGuestItem, isZh: boolean) => {
  const nameCN = guest.NameCN || guest.nameCn;
  const nameEN = guest.NameEN || guest.nameEn;
  return isZh ? nameCN || nameEN || '' : nameEN || nameCN || '';
};

const getGuestTitle = (guest: ConferenceAgendaGuestItem, isZh: boolean) => {
  const titles = guest.Titles || guest.titles || [];
  const titleText = titles
    .map((item) => getLocaleText(isZh, item.TitleCN, item.TitleEN))
    .filter(Boolean)
    .join(' · ');
  return (
    titleText ||
    guest.Position ||
    guest.position ||
    guest.Organization ||
    guest.organization ||
    ''
  );
};

const getGuestID = (guest?: ConferenceAgendaGuestItem) =>
  guest?.GuestID || guest?.guestId || '';

const getGuestDataSource = (guest?: ConferenceAgendaGuestItem) =>
  guest?.GuestDataSource || guest?.guestDataSource || '';

const isGuestContactAllowed = (guest?: ConferenceAgendaGuestItem) =>
  (guest?.AllowContact ?? guest?.allowContact ?? 1) !== 0;

const getAgendaTitle = (agenda: ConferenceAgendaItem, isZh: boolean) =>
  getLocaleText(
    isZh,
    agenda.TitleCN || agenda.titleCn,
    agenda.TitleEN || agenda.titleEn
  );

const MOBILE_MEDIA_QUERY = '(max-width: 768px)';
const MOBILE_HEADER_HEIGHT = 48;
const MOBILE_PUBLIC_BANNER_HEIGHT = 48;
const FORUM_TABS_STICKY_GAP = 16;
const MOBILE_FORUM_TAB_EDGE_GAP = 16;
const FORUM_TABS_SCROLL_EDGE_TOLERANCE = 2;

interface PublicBannerMetrics {
  top: number;
  height: number;
}

interface ForumTabsFixedState {
  isFixed: boolean;
  top: number;
  placeholderHeight: number;
}

interface ForumTabsScrollState {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

const DEFAULT_FORUM_TABS_FIXED_STATE: ForumTabsFixedState = {
  isFixed: false,
  top: 0,
  placeholderHeight: 0,
};

const DEFAULT_FORUM_TABS_SCROLL_STATE: ForumTabsScrollState = {
  canScrollLeft: false,
  canScrollRight: false,
};

const DEFAULT_PUBLIC_BANNER_METRICS: PublicBannerMetrics = {
  top: MOBILE_HEADER_HEIGHT,
  height: MOBILE_PUBLIC_BANNER_HEIGHT,
};

const getPublicBannerMetrics = (): PublicBannerMetrics => {
  // 会议页只渲染一个 PublicBanner；AgendaSection 通过这个标记读取其吸顶位置。
  // 如果后续页面出现多个 PublicBanner，需要改为由父组件显式传入测量结果。
  const publicBanner = document.querySelector<HTMLElement>(
    '[data-public-banner]'
  );
  if (!publicBanner) {
    return DEFAULT_PUBLIC_BANNER_METRICS;
  }

  const computedTop = Number.parseFloat(
    window.getComputedStyle(publicBanner).top
  );
  const rect = publicBanner.getBoundingClientRect();

  return {
    top: Number.isFinite(computedTop) ? computedTop : MOBILE_HEADER_HEIGHT,
    height:
      rect.height || publicBanner.offsetHeight || MOBILE_PUBLIC_BANNER_HEIGHT,
  };
};

const clampScrollLeft = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const AgendaTabsArrowIcon = ({
  direction,
}: {
  direction: 'left' | 'right';
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="6"
    height="12"
    viewBox="0 0 8 14"
    fill="none"
    aria-hidden="true"
    style={{
      width: '6px',
      height: '12px',
      transform: direction === 'left' ? 'rotate(180deg)' : undefined,
    }}
  >
    <path
      d="M0.636719 0.63623L6.63672 6.63623L0.636719 12.6362"
      stroke="#121C58"
      strokeWidth="1.8"
    />
  </svg>
);

const normalizeForumTabsScrollLeft = (value: number, maxScrollLeft: number) => {
  if (maxScrollLeft <= FORUM_TABS_SCROLL_EDGE_TOLERANCE) {
    return 0;
  }

  const nextValue = clampScrollLeft(value, 0, maxScrollLeft);
  if (nextValue <= FORUM_TABS_SCROLL_EDGE_TOLERANCE) {
    return 0;
  }
  if (maxScrollLeft - nextValue <= FORUM_TABS_SCROLL_EDGE_TOLERANCE) {
    return maxScrollLeft;
  }
  return nextValue;
};

const getForumTabsTargetScrollLeft = (
  forumTabs: HTMLDivElement,
  direction: 'left' | 'right'
) => {
  const maxScrollLeft = forumTabs.scrollWidth - forumTabs.clientWidth;
  if (maxScrollLeft <= FORUM_TABS_SCROLL_EDGE_TOLERANCE) {
    return 0;
  }

  const containerRect = forumTabs.getBoundingClientRect();
  const tabs = Array.from(forumTabs.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement
  );
  const tabRects = tabs.map((tab) => {
    const rect = tab.getBoundingClientRect();
    const left = rect.left - containerRect.left + forumTabs.scrollLeft;
    const width = rect.width;
    return {
      left,
      right: left + width,
      width,
    };
  });

  if (direction === 'right') {
    const visibleRight = forumTabs.scrollLeft + forumTabs.clientWidth;
    const targetIndex = tabRects.findIndex(
      (tabRect) =>
        tabRect.right > visibleRight + FORUM_TABS_SCROLL_EDGE_TOLERANCE
    );
    if (targetIndex === -1 || targetIndex === tabRects.length - 1) {
      return maxScrollLeft;
    }

    const targetTab = tabRects[targetIndex];
    const nextTab = tabRects[targetIndex + 1];
    const desiredVisibleRight = nextTab.left + nextTab.width / 2;
    const minScrollLeft = targetTab.right - forumTabs.clientWidth;
    const maxScrollLeftForTarget = targetTab.left;
    const targetScrollLeft = clampScrollLeft(
      desiredVisibleRight - forumTabs.clientWidth,
      minScrollLeft,
      maxScrollLeftForTarget
    );

    return normalizeForumTabsScrollLeft(targetScrollLeft, maxScrollLeft);
  }

  const visibleLeft = forumTabs.scrollLeft;
  let targetIndex = -1;
  for (let index = tabRects.length - 1; index >= 0; index -= 1) {
    if (tabRects[index].left < visibleLeft - FORUM_TABS_SCROLL_EDGE_TOLERANCE) {
      targetIndex = index;
      break;
    }
  }

  if (targetIndex <= 0) {
    return 0;
  }

  const targetTab = tabRects[targetIndex];
  const previousTab = tabRects[targetIndex - 1];
  const desiredVisibleLeft = previousTab.right - previousTab.width / 2;
  const minScrollLeft = targetTab.right - forumTabs.clientWidth;
  const maxScrollLeftForTarget = targetTab.left;
  const targetScrollLeft = clampScrollLeft(
    desiredVisibleLeft,
    minScrollLeft,
    maxScrollLeftForTarget
  );

  return normalizeForumTabsScrollLeft(targetScrollLeft, maxScrollLeft);
};

const scrollMobileForumTabIntoView = (
  forumTabs: HTMLDivElement,
  forumTab: HTMLElement
) => {
  if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
    return;
  }

  const maxScrollLeft = forumTabs.scrollWidth - forumTabs.clientWidth;
  if (maxScrollLeft <= FORUM_TABS_SCROLL_EDGE_TOLERANCE) {
    return;
  }

  const containerRect = forumTabs.getBoundingClientRect();
  const tabRect = forumTab.getBoundingClientRect();
  const tabLeft = tabRect.left - containerRect.left + forumTabs.scrollLeft;
  const tabRight = tabLeft + tabRect.width;
  const contentEdgeGap =
    forumTabs.offsetLeft < 0 ? MOBILE_FORUM_TAB_EDGE_GAP : 0;
  const visibleLeft = forumTabs.scrollLeft + contentEdgeGap;
  const visibleRight =
    forumTabs.scrollLeft + forumTabs.clientWidth - contentEdgeGap;
  let targetScrollLeft: number | undefined;

  if (tabLeft < visibleLeft) {
    targetScrollLeft = tabLeft - contentEdgeGap;
  } else if (tabRight > visibleRight) {
    targetScrollLeft = tabRight - forumTabs.clientWidth + contentEdgeGap;
  }

  if (targetScrollLeft === undefined) {
    return;
  }

  forumTabs.scrollTo({
    left: normalizeForumTabsScrollLeft(targetScrollLeft, maxScrollLeft),
    behavior: 'smooth',
  });
};

const GuestActionIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle
      cx="12"
      cy="12"
      r="11.808"
      fill="var(--agenda-chat-icon-fill, #EBF1FF)"
      stroke="var(--agenda-chat-icon-border, #D7E1F7)"
      strokeWidth="0.384"
    />
    <path
      d="M5.75391 8.16703C5.75391 7.63684 6.18371 7.20703 6.71391 7.20703H16.7939C17.3241 7.20703 17.7539 7.63684 17.7539 8.16703V14.6763C17.7539 15.2065 17.3241 15.6363 16.7939 15.6363H7.99835C7.88988 15.6363 7.7846 15.673 7.69969 15.7405L5.75391 17.287V8.16703Z"
      fill="var(--agenda-chat-icon-message, #0040DD)"
      fillOpacity="var(--agenda-chat-icon-message-opacity, 0.7)"
    />
    <circle
      cx="8.87625"
      cy="11.2766"
      r="0.72"
      fill="var(--agenda-chat-icon-fill, #EBF1FF)"
    />
    <circle
      cx="11.7591"
      cy="11.2766"
      r="0.72"
      fill="var(--agenda-chat-icon-fill, #EBF1FF)"
    />
    <circle
      cx="14.638"
      cy="11.2766"
      r="0.72"
      fill="var(--agenda-chat-icon-fill, #EBF1FF)"
    />
  </svg>
);
const BeijingTimeIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M16.5 9C16.5 13.14 13.14 16.5 9 16.5C4.86 16.5 1.5 13.14 1.5 9C1.5 4.86 4.86 1.5 9 1.5C13.14 1.5 16.5 4.86 16.5 9Z"
      stroke="#616366"
      strokeWidth="1.125"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.7827 11.3848L9.45766 9.99732C9.05266 9.75732 8.72266 9.17982 8.72266 8.70732V5.63232"
      stroke="#616366"
      strokeWidth="1.125"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const buildAgendaGuestHoverKey = (guest?: ConferenceAgendaGuestItem) =>
  `${getGuestDataSource(guest) || 'cn'}-${getGuestID(guest)}`;

/**
 * 大会议程组件：接口一次返回论坛树，前端只负责默认选中和 PC/移动端两套展示。
 */
export default function AgendaSection({
  data,
  title,
  site,
  years,
}: AgendaSectionProps) {
  const locale = useLocale();
  const isZh = site ? site === 'zh' : locale === 'zh';
  const localeClassName = isZh ? styles.zhSection : styles.enSection;
  const authApi = getConferenceChatAuthApiBySite(site);
  const [, startTransition] = useTransition();
  const requestIdRef = useRef(0);
  const [sectionData, setSectionData] = useState<ConferenceAgendaDetailResp>(
    data || { list: [] }
  );
  const [selectedYear, setSelectedYear] = useState(
    () => years?.[0] || new Date().getFullYear()
  );
  const forums = useMemo(() => getForumList(sectionData), [sectionData]);
  const [chatAuth, setChatAuth] = useState<ConferenceChatAuthInfo>();
  const [activeForumID, setActiveForumID] = useState('');
  const [forumTabsFixedState, setForumTabsFixedState] =
    useState<ForumTabsFixedState>(DEFAULT_FORUM_TABS_FIXED_STATE);
  const [forumTabsScrollState, setForumTabsScrollState] =
    useState<ForumTabsScrollState>(DEFAULT_FORUM_TABS_SCROLL_STATE);
  const [suppressedHoverGuestKey, setSuppressedHoverGuestKey] = useState('');
  const sectionRef = useRef<HTMLElement>(null);
  const forumTabsSlotRef = useRef<HTMLDivElement>(null);
  const forumTabsRef = useRef<HTMLDivElement>(null);
  const forumTabsFixedStateRef = useRef(forumTabsFixedState);
  const forumTabsScrollStateRef = useRef(forumTabsScrollState);
  const publicBannerMetricsRef = useRef<PublicBannerMetrics>(
    DEFAULT_PUBLIC_BANNER_METRICS
  );

  useEffect(() => {
    setSectionData(data || { list: [] });
    if (years?.[0]) {
      setSelectedYear(years[0]);
    }
  }, [data, years]);

  const activeForum =
    forums.find((forum, index) => getForumID(forum, index) === activeForumID) ||
    forums[0];
  const sessions = useMemo(() => getSessionList(activeForum), [activeForum]);

  useEffect(() => {
    const nextForum = forums[0];
    // 切换年份或接口数据刷新后，默认回到第一个上线论坛，避免沿用上一届无效 ID。
    setActiveForumID(nextForum ? getForumID(nextForum, 0) : '');
  }, [forums]);

  useEffect(() => {
    let cancelled = false;
    const loadChatAuth = async () => {
      try {
        const payload = await fetchConferenceChatAuthInfo(authApi);
        if (!cancelled && !payload.loginRequired) {
          setChatAuth(payload.data);
        }
      } catch (error) {
        if (!cancelled) {
          setChatAuth(undefined);
        }
      }
    };
    void loadChatAuth();
    return () => {
      cancelled = true;
    };
  }, [authApi]);

  useEffect(() => {
    forumTabsFixedStateRef.current = forumTabsFixedState;
  }, [forumTabsFixedState]);

  useEffect(() => {
    forumTabsScrollStateRef.current = forumTabsScrollState;
  }, [forumTabsScrollState]);

  useEffect(() => {
    let frameID = 0;
    let resizeObserver: ResizeObserver | undefined;

    const commitState = (nextState: ForumTabsScrollState) => {
      const previousState = forumTabsScrollStateRef.current;
      if (
        previousState.canScrollLeft === nextState.canScrollLeft &&
        previousState.canScrollRight === nextState.canScrollRight
      ) {
        return;
      }

      forumTabsScrollStateRef.current = nextState;
      setForumTabsScrollState(nextState);
    };

    const updateForumTabsScrollState = () => {
      frameID = 0;

      const forumTabs = forumTabsRef.current;
      if (!forumTabs) {
        commitState(DEFAULT_FORUM_TABS_SCROLL_STATE);
        return;
      }

      const maxScrollLeft = forumTabs.scrollWidth - forumTabs.clientWidth;
      const hasOverflow = maxScrollLeft > 1;
      const scrollLeft = normalizeForumTabsScrollLeft(
        forumTabs.scrollLeft,
        maxScrollLeft
      );

      commitState({
        canScrollLeft: hasOverflow && scrollLeft > 0,
        canScrollRight: hasOverflow && scrollLeft < maxScrollLeft,
      });
    };

    const scheduleUpdate = () => {
      if (frameID) return;
      frameID = window.requestAnimationFrame(updateForumTabsScrollState);
    };

    const forumTabs = forumTabsRef.current;
    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);
    forumTabs?.addEventListener('scroll', scheduleUpdate, { passive: true });

    if (forumTabs && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(forumTabs);
    }

    return () => {
      if (frameID) {
        window.cancelAnimationFrame(frameID);
      }
      window.removeEventListener('resize', scheduleUpdate);
      forumTabs?.removeEventListener('scroll', scheduleUpdate);
      resizeObserver?.disconnect();
    };
  }, [forums]);

  useEffect(() => {
    const mobileMedia = window.matchMedia(MOBILE_MEDIA_QUERY);
    let frameID = 0;

    const updatePublicBannerMetrics = () => {
      publicBannerMetricsRef.current = getPublicBannerMetrics();
    };

    const commitState = (nextState: ForumTabsFixedState) => {
      const previousState = forumTabsFixedStateRef.current;
      if (
        previousState.isFixed === nextState.isFixed &&
        previousState.top === nextState.top &&
        previousState.placeholderHeight === nextState.placeholderHeight
      ) {
        return;
      }

      forumTabsFixedStateRef.current = nextState;
      setForumTabsFixedState(nextState);
    };

    const updateForumTabsFixedState = () => {
      frameID = 0;

      const section = sectionRef.current;
      const tabsSlot = forumTabsSlotRef.current;
      const forumTabs = forumTabsRef.current;

      if (!mobileMedia.matches || !section || !tabsSlot || !forumTabs) {
        commitState(DEFAULT_FORUM_TABS_FIXED_STATE);
        return;
      }

      const { top: publicBannerTop, height: publicBannerHeight } =
        publicBannerMetricsRef.current;
      const fixedTop = Math.round(publicBannerTop + publicBannerHeight);
      const tabsRect = forumTabs.getBoundingClientRect();
      const tabsHeight = Math.ceil(tabsRect.height);
      const naturalTabsHeight = forumTabsFixedStateRef.current.isFixed
        ? forumTabsFixedStateRef.current.placeholderHeight
        : tabsHeight;
      const fixedTabsHeight = tabsHeight + FORUM_TABS_STICKY_GAP * 2;
      const tabsSlotTop = tabsSlot.getBoundingClientRect().top;
      const sectionBottom = section.getBoundingClientRect().bottom;
      const shouldFixTabs =
        tabsSlotTop <= fixedTop + FORUM_TABS_STICKY_GAP &&
        sectionBottom > fixedTop + fixedTabsHeight;

      commitState(
        shouldFixTabs
          ? {
              isFixed: true,
              top: fixedTop,
              placeholderHeight: naturalTabsHeight,
            }
          : {
              isFixed: false,
              top: fixedTop,
              placeholderHeight: 0,
            }
      );
    };

    const scheduleUpdate = () => {
      if (frameID) return;
      frameID = window.requestAnimationFrame(updateForumTabsFixedState);
    };

    const handleViewportChange = () => {
      updatePublicBannerMetrics();
      scheduleUpdate();
    };

    // PublicBanner 高度/吸顶 top 只在初始化和视口变化时更新，避免滚动热路径重复读取它。
    updatePublicBannerMetrics();
    scheduleUpdate();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);

    if (mobileMedia.addEventListener) {
      mobileMedia.addEventListener('change', handleViewportChange);
    } else {
      mobileMedia.addListener(handleViewportChange);
    }

    return () => {
      if (frameID) {
        window.cancelAnimationFrame(frameID);
      }
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      if (mobileMedia.removeEventListener) {
        mobileMedia.removeEventListener('change', handleViewportChange);
      } else {
        mobileMedia.removeListener(handleViewportChange);
      }
    };
  }, []);

  const handleForumChange = (
    event: React.MouseEvent<HTMLButtonElement>,
    forum: ConferenceAgendaForumItem,
    index: number
  ) => {
    const forumID = getForumID(forum, index);
    if (forumTabsRef.current) {
      scrollMobileForumTabIntoView(forumTabsRef.current, event.currentTarget);
    }

    if (forumID === activeForumID) {
      return;
    }

    setActiveForumID(forumID);

    if (!forumTabsFixedStateRef.current.isFixed || !forumTabsSlotRef.current) {
      return;
    }

    const { top: publicBannerTop, height: publicBannerHeight } =
      publicBannerMetricsRef.current;
    const fixedTop = Math.round(publicBannerTop + publicBannerHeight);
    const tabsSlotTop = forumTabsSlotRef.current.getBoundingClientRect().top;
    const targetScrollTop =
      window.scrollY + tabsSlotTop - fixedTop - FORUM_TABS_STICKY_GAP;

    window.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth',
    });
  };

  const handleGuestAction = (
    event: React.MouseEvent<HTMLButtonElement>,
    guest?: ConferenceAgendaGuestItem
  ) => {
    event.preventDefault();
    event.stopPropagation();
    // 新标签打开后当前页的 hover 不会自动撤销，这里先压住当前嘉宾入口的悬浮态，
    // 等鼠标再次移动或离开时再恢复，避免从会聊标签切回后仍保持高亮。
    setSuppressedHoverGuestKey(buildAgendaGuestHoverKey(guest));
    event.currentTarget.blur();
    const contactId = getGuestID(guest);
    const guestDataSource = getGuestDataSource(guest);
    if (
      isConferenceChatGuestSelf(chatAuth, {
        contactId,
        guestDataSource,
      })
    ) {
      return;
    }
    void openConferenceChatEntry({
      authApi,
      site,
      // 议程里的嘉宾会聊入口同样新开标签，避免用户离开当前浏览的议程位置。
      openInNewTab: true,
      targetSearchParams:
        contactId && guestDataSource
          ? {
              tab: 'messages',
              contactId,
              guestDataSource,
            }
          : undefined,
    });
  };

  const resetSuppressedHoverGuest = (guest?: ConferenceAgendaGuestItem) => {
    const hoverKey = buildAgendaGuestHoverKey(guest);
    setSuppressedHoverGuestKey((currentKey) =>
      currentKey === hoverKey ? '' : currentKey
    );
  };

  const handleForumTabsScrollButtonClick = (direction: 'left' | 'right') => {
    const forumTabs = forumTabsRef.current;
    if (!forumTabs) {
      return;
    }

    forumTabs.scrollTo({
      left: getForumTabsTargetScrollLeft(forumTabs, direction),
      behavior: 'smooth',
    });
  };

  const handleYearChange = (year: number) => {
    if (year === selectedYear) {
      return;
    }
    const previousYear = selectedYear;
    setSelectedYear(year);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    startTransition(() => {
      void fetchConferenceAgendaDetail({
        conferenceYear: year,
        site,
      })
        .then((nextData) => {
          if (requestIdRef.current === requestId) {
            setSectionData(nextData);
          }
        })
        .catch((error) => {
          console.error('[conference agenda year change error]', error);
          if (requestIdRef.current === requestId) {
            setSelectedYear(previousYear);
          }
        });
    });
  };

  const renderGuest = (guest: ConferenceAgendaGuestItem, index: number) => {
    const hoverKey = buildAgendaGuestHoverKey(guest);
    const isHoverSuppressed = suppressedHoverGuestKey === hoverKey;
    const name = getGuestName(guest, isZh);
    const titleText = getGuestTitle(guest, isZh);
    const titleDisplay = titleText || '';
    const avatar = guest.Avatar || guest.avatar;
    const contactId = getGuestID(guest);
    const guestDataSource = getGuestDataSource(guest);
    const showChatEntry =
      isGuestContactAllowed(guest) &&
      !isConferenceChatGuestSelf(chatAuth, {
        contactId,
        guestDataSource,
      });
    return (
      <div
        className={styles.guest}
        key={`${guest.GuestID || guest.guestId || index}`}
        onMouseMove={() => resetSuppressedHoverGuest(guest)}
        onMouseLeave={() => resetSuppressedHoverGuest(guest)}
      >
        <div
          className={`${styles.guestAvatarWrap} ${
            isHoverSuppressed ? styles.guestAvatarWrapHoverSuppressed : ''
          }`}
        >
          {avatar ? (
            <Image
              src={avatar}
              alt={name}
              className={styles.guestAvatar}
              loading="lazy"
            />
          ) : (
            <span className={styles.guestAvatarFallback}>
              {name.slice(0, 1) || '-'}
            </span>
          )}
          {showChatEntry && (
            <Tooltip
              content={isZh ? '和嘉宾聊天' : 'Chat with guest'}
              placement="top"
              destroyOnClose
              showArrow
              theme="default"
            >
              <button
                type="button"
                className={`${styles.guestAvatarAction} ${
                  isHoverSuppressed
                    ? styles.guestAvatarActionHoverSuppressed
                    : ''
                }`}
                onClick={(event) => handleGuestAction(event, guest)}
                aria-label={isZh ? '和嘉宾聊天' : 'Chat with guest'}
              >
                <GuestActionIcon />
              </button>
            </Tooltip>
          )}
        </div>
        <div className={styles.guestText}>
          {name ? <strong>{name}</strong> : null}
          {name && titleDisplay ? (
            <div className={styles.guestDivider} aria-hidden="true"></div>
          ) : null}
          {titleDisplay ? <em>{titleDisplay}</em> : null}
        </div>
        {showChatEntry && (
          <button
            type="button"
            className={styles.guestMobileAction}
            onClick={(event) => handleGuestAction(event, guest)}
            aria-label={isZh ? '和嘉宾聊天' : 'Chat with guest'}
          >
            <GuestActionIcon />
          </button>
        )}
      </div>
    );
  };

  const renderAgendaCard = (agenda: ConferenceAgendaItem, index: number) => {
    const guests = getGuestList(agenda);
    const hasGuests = guests.length > 0;
    const agendaTitle = getAgendaTitle(agenda, isZh);
    return (
      <article className={styles.agendaCard} key={getAgendaID(agenda, index)}>
        <div
          className={`${styles.agendaCardHeader} ${
            hasGuests ? '' : styles.agendaCardHeaderWithoutGuests
          }`.trim()}
        >
          {agendaTitle ? <h4>{agendaTitle}</h4> : null}
        </div>
        {hasGuests && (
          <div className={styles.guestList}>
            {guests.map((guest, guestIndex) => renderGuest(guest, guestIndex))}
          </div>
        )}
      </article>
    );
  };

  const renderSessionSummary = (
    session: ConferenceAgendaSessionItem,
    index: number
  ) => {
    const sessionTime = formatConferenceAgendaClockRange(
      session.StartTime ?? session.startTime,
      session.EndTime ?? session.endTime
    );
    const sessionTitle = getLocaleText(
      isZh,
      session.TitleCN || session.titleCn,
      session.TitleEN || session.titleEn
    );
    return (
      <div className={styles.sessionSummary} key={getSessionID(session, index)}>
        {sessionTitle ? <strong>{sessionTitle}</strong> : null}
        {sessionTime && <span>{sessionTime}</span>}
      </div>
    );
  };

  const renderSessionAgendaRow = (
    session: ConferenceAgendaSessionItem,
    sessionIndex: number
  ) => {
    const agendaList = getAgendaList(session);
    return (
      <section
        className={styles.pcSessionRow}
        key={getSessionID(session, sessionIndex)}
      >
        {renderSessionSummary(session, sessionIndex)}
        <div className={styles.sessionAgendaPanel}>
          {agendaList.length ? (
            agendaList.map((agenda, agendaIndex) =>
              renderAgendaCard(agenda, agendaIndex)
            )
          ) : (
            <div className={styles.empty}>
              {isZh ? '暂无议程' : 'No agenda'}
            </div>
          )}
        </div>
      </section>
    );
  };

  return (
    <section
      id="agenda"
      ref={sectionRef}
      className={`${styles.section} ${localeClassName}`}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <SectionTitle
            className={
              'mb-10 mobile:mb-5 text-black mobile:!text-[24px] leading-[67px] mobile:leading-[34px]' +
              (isZh
                ? ''
                : ' !font-700 !leading-[72px] !mb-12 mobile:!mb-5 mobile:!text-[24px] mobile:!leading-[29px]')
            }
          >
            {title}
          </SectionTitle>
          <SectionYearSelector
            selectedYear={selectedYear}
            years={years || []}
            onChange={handleYearChange}
          />
        </div>

        {forums.length > 0 ? (
          <>
            <div
              ref={forumTabsSlotRef}
              style={
                forumTabsFixedState.placeholderHeight
                  ? { height: forumTabsFixedState.placeholderHeight }
                  : undefined
              }
            >
              <div
                className={`${styles.forumTabsWrap} ${
                  forumTabsFixedState.isFixed ? styles.forumTabsWrapFixed : ''
                }`}
                style={
                  forumTabsFixedState.top
                    ? ({
                        '--agenda-forum-tabs-fixed-top': `${forumTabsFixedState.top}px`,
                      } as React.CSSProperties)
                    : undefined
                }
              >
                <div ref={forumTabsRef} className={styles.forumTabs}>
                  {forums.map((forum, index) => {
                    const forumID = getForumID(forum, index);
                    const isActive = forumID === activeForumID;
                    const forumTitle = getLocaleText(
                      isZh,
                      forum.TitleCN || forum.titleCn,
                      forum.TitleEN || forum.titleEn
                    );
                    const forumSubtitle = getLocaleText(
                      isZh,
                      forum.SubtitleCN || forum.subtitleCn,
                      forum.SubtitleEN || forum.subtitleEn
                    );
                    return (
                      <button
                        key={forumID}
                        type="button"
                        className={`${styles.forumTab} ${
                          isActive ? styles.forumTabActive : ''
                        }`}
                        onClick={(event) =>
                          handleForumChange(event, forum, index)
                        }
                      >
                        {forumTitle ? <span>{forumTitle}</span> : null}
                        {forumSubtitle ? <small>{forumSubtitle}</small> : null}
                      </button>
                    );
                  })}
                </div>
                {forumTabsScrollState.canScrollLeft && (
                  <>
                    <span
                      className={`${styles.forumTabsFade} ${styles.forumTabsFadeLeft}`}
                      aria-hidden="true"
                    />
                    <button
                      type="button"
                      className={`${styles.forumTabsArrow} ${styles.forumTabsArrowLeft}`}
                      onClick={() => handleForumTabsScrollButtonClick('left')}
                      aria-label={
                        isZh ? '向左滚动论坛列表' : 'Scroll forum tabs left'
                      }
                    >
                      <AgendaTabsArrowIcon direction="left" />
                    </button>
                  </>
                )}
                {forumTabsScrollState.canScrollRight && (
                  <>
                    <span
                      className={`${styles.forumTabsFade} ${styles.forumTabsFadeRight}`}
                      aria-hidden="true"
                    />
                    <button
                      type="button"
                      className={`${styles.forumTabsArrow} ${styles.forumTabsArrowRight}`}
                      onClick={() => handleForumTabsScrollButtonClick('right')}
                      aria-label={
                        isZh ? '向右滚动论坛列表' : 'Scroll forum tabs right'
                      }
                    >
                      <AgendaTabsArrowIcon direction="right" />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className={styles.forumTabsBorder}></div>
            {!isZh && (
              <div className={styles.pcBeijingTime}>
                <BeijingTimeIcon />
                <span>Beijing Time</span>
              </div>
            )}

            <div className={styles.pcAgendaLayout}>
              {sessions.length ? (
                sessions.map((session, index) =>
                  renderSessionAgendaRow(session, index)
                )
              ) : (
                <div className={styles.empty}>
                  {isZh ? '暂无议程' : 'No agenda'}
                </div>
              )}
            </div>

            {!isZh && (
              <div className={styles.mobileBeijingTime}>
                <BeijingTimeIcon />
                <span>Beijing Time</span>
              </div>
            )}

            <div className={styles.mobileAgendaLayout}>
              {sessions.length ? (
                sessions.map((session, sessionIndex) => {
                  const agendaList = getAgendaList(session);
                  const sessionTime = formatConferenceAgendaClockRange(
                    session.StartTime ?? session.startTime,
                    session.EndTime ?? session.endTime
                  );
                  const sessionTitle = getLocaleText(
                    isZh,
                    session.TitleCN || session.titleCn,
                    session.TitleEN || session.titleEn
                  );
                  return (
                    <section
                      className={styles.mobileSession}
                      key={getSessionID(session, sessionIndex)}
                    >
                      <header>
                        {sessionTitle ? <strong>{sessionTitle}</strong> : null}
                        {sessionTime && <span>{sessionTime}</span>}
                      </header>
                      {agendaList.length ? (
                        agendaList.map((agenda, index) =>
                          renderAgendaCard(agenda, index)
                        )
                      ) : (
                        <div className={styles.empty}>
                          {isZh ? '暂无议程' : 'No agenda'}
                        </div>
                      )}
                    </section>
                  );
                })
              ) : (
                <div className={styles.empty}>
                  {isZh ? '暂无议程' : 'No agenda'}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className={styles.empty}>{isZh ? '暂无议程' : 'No agenda'}</div>
        )}
      </div>
    </section>
  );
}
